import { useEffect } from 'react';

import clsx from 'clsx';

import { useAppContext } from 'context/AppContext';
import { isServer } from 'helpers/canUse';
import { useOnScreen } from 'hooks';

import Placeholder from './components/Placeholder';

import classes from './LazyContent.module.scss';

const noop = () => {};

/** @type {import("./types").LazyPlaceholder} */
const LazyPlaceholder = ({ columns, placeholderClassname, minHeight }) => {
  const columnsToMap = [...new Array(columns)].map((_, i) => i);
  return columns ? (
    columnsToMap.map((column) => (
      <Placeholder
        key={`placeholder-${column}`}
        className={clsx(
          placeholderClassname,
          classes.placeholderColumn,
          classes[`placeholderColumn${columns}`]
        )}
      />
    ))
  ) : (
    <Placeholder
      style={{ minHeight }}
      className={clsx(classes.placeholder, placeholderClassname)}
    />
  );
};

/** @type {import("./types").default} */
const LazyContent = ({
  children,
  className,
  columns = null,
  index,
  minHeight = 0,
  placeholderClassname,
  onVisible = noop,
  rootMargin,
  renderImmediately = false,
  ssr,
  ...moreProps
}) => {
  const [onScreen, theRef] = useOnScreen({ rootMargin });
  const { isCypressTakingSnapshots } = useAppContext();

  const shouldRender = renderImmediately || onScreen || (ssr && isServer);

  useEffect(() => {
    if (onScreen) {
      onVisible({ index });
    }
    return () => {};
  }, [onScreen, onVisible, index]);

  if (isCypressTakingSnapshots) {
    return <div>{children}</div>;
  }

  return (
    <div
      ref={theRef}
      className={clsx('LazyContent', {
        [className]: !shouldRender,
        [classes.placeholderColumns]: columns && !shouldRender,
      })}
      {...moreProps}
    >
      {shouldRender ? (
        children
      ) : (
        <LazyPlaceholder
          minHeight={minHeight}
          columns={columns}
          placeholderClassname={placeholderClassname}
        />
      )}
    </div>
  );
};

export default LazyContent;
